import { EMPTY, from, mergeMap, Observable, reduce } from 'rxjs';
import { map } from 'rxjs/operators';

function fileFromEntry$(entry: FileSystemFileEntry): Observable<File> {
  return new Observable<File>((subscriber) => {
    entry.file(
      (file) => {
        subscriber.next(file);
        subscriber.complete();
      },
      (err) => subscriber.error(err),
    );
  });
}

function readDirectoryBatch$(reader: FileSystemDirectoryReader): Observable<FileSystemEntry[]> {
  return new Observable<FileSystemEntry[]>((subscriber) => {
    reader.readEntries(
      (entries) => {
        subscriber.next(entries);
        subscriber.complete();
      },
      (err) => subscriber.error(err),
    );
  });
}

function readAllDirectoryEntries$(reader: FileSystemDirectoryReader): Observable<FileSystemEntry[]> {
  return readDirectoryBatch$(reader).pipe(
    mergeMap((entries) => {
      if (entries.length === 0) return from([[]]);

      return readAllDirectoryEntries$(reader).pipe(map((rest) => [...entries, ...rest]));
    }),
  );
}

function readEntryRecursively$(entry: FileSystemEntry, parentPath: string): Observable<File[]> {
  if (entry.name.startsWith('.')) {
    return from([[]]);
  }

  if (entry.isFile) {
    return fileFromEntry$(entry as FileSystemFileEntry).pipe(
      map((file) => {
        if (parentPath) {
          Object.defineProperty(file, 'webkitRelativePath', {
            value: `${parentPath}/${file.name}`,
            writable: false,
            configurable: true,
          });
        }

        return [file];
      }),
    );
  }

  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const dirPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;

    return readAllDirectoryEntries$(reader).pipe(
      mergeMap((entries) => from(entries)),
      mergeMap((child) => readEntryRecursively$(child, dirPath)),
      reduce<File[], File[]>((acc, files) => [...acc, ...files], []),
    );
  }

  return from([[]]);
}

export function readDroppedEntries(items: DataTransferItemList): Observable<File[]> {
  const entries: FileSystemEntry[] = [];

  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry();

    if (entry) {
      entries.push(entry);
    }
  }

  if (!entries.length) return EMPTY;

  return from(entries).pipe(
    mergeMap((entry) => readEntryRecursively$(entry, '')),
    reduce<File[], File[]>((acc, files) => [...acc, ...files], []),
  );
}
